import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Lock, Plus, Trash2, Edit3, ExternalLink, Save, X, Upload, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'uandfashion_admin_key';
const EMPTY = {
  slug: '',
  title_nl: '',
  title_en: '',
  excerpt_nl: '',
  excerpt_en: '',
  body_nl: '',
  body_en: '',
  hero_image: '',
  tags: '',
  published: true,
};

const AdminBlogPage = () => {
  const [keyInput, setKeyInput] = useState('');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const heroInputRef = useRef(null);

  const fetchList = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/posts/admin/list`, { params: { key } });
      setPosts(res.data || []);
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthKey(key);
    } catch (err) {
      setError(err?.response?.status === 403 ? 'Onjuist wachtwoord.' : 'Kan de lijst niet ophalen.');
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthKey('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authKey) fetchList(authKey);
    const params = new URLSearchParams(window.location.search);
    const urlKey = params.get('key');
    if (urlKey && !authKey) fetchList(urlKey);
  }, [authKey, fetchList]);

  const handleAuth = (e) => {
    e.preventDefault();
    if (keyInput.trim()) fetchList(keyInput.trim());
  };
  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      slug: p.slug || '',
      title_nl: p.title_nl || '',
      title_en: p.title_en || '',
      excerpt_nl: p.excerpt_nl || '',
      excerpt_en: p.excerpt_en || '',
      body_nl: p.body_nl || '',
      body_en: p.body_en || '',
      hero_image: p.hero_image || '',
      tags: (p.tags || []).join(', '),
      published: p.published !== false,
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const handleHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploadingHero(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await axios.post(`${API}/uploads`, data, {
        params: { key: authKey },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, hero_image: `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` }));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload mislukt.');
    } finally {
      setUploadingHero(false);
      if (heroInputRef.current) heroInputRef.current.value = '';
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (editingId) await axios.patch(`${API}/posts/${editingId}`, payload, { params: { key: authKey } });
      else await axios.post(`${API}/posts`, payload, { params: { key: authKey } });
      closeForm();
      fetchList(authKey);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Verwijder "${p.title_nl}"?`)) return;
    try {
      await axios.delete(`${API}/posts/${p.id}`, { params: { key: authKey } });
      fetchList(authKey);
    } catch { setError('Verwijderen mislukt.'); }
  };

  if (!authKey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50/40 to-indigo-50/40 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-10">
          <div className="text-center mb-6">
            <Lock className="w-8 h-8 text-purple-500 mx-auto mb-3" />
            <h1 className="text-2xl font-medium text-gray-900">Beheer — Inspiratie</h1>
          </div>
          <form onSubmit={handleAuth} className="space-y-4">
            <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="Beheerder-sleutel"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg" autoFocus data-testid="blog-admin-key-input" />
            <button type="submit" className="w-full bg-gray-900 text-white py-3 rounded-lg" data-testid="blog-admin-submit">Ontgrendel</button>
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-blog-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-light text-gray-900">Inspiratie beheer</h1>
            <p className="text-sm text-gray-500 mt-1">Beheer je blog-artikels (Style Journal).</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => fetchList(authKey)} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm">
              <RefreshCw className="w-4 h-4" />Ververs
            </button>
            <button onClick={openCreate} className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2 rounded-full text-sm font-medium" data-testid="blog-add-btn">
              <Plus className="w-4 h-4" />Nieuw artikel
            </button>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}

        {loading ? <div className="text-center py-16 text-gray-500">Laden…</div> : posts.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-500 border border-gray-100">
            <p className="mb-4">Nog geen artikels. Voeg je eerste inspiratie-artikel toe.</p>
            <button onClick={openCreate} className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm">
              <Plus className="w-4 h-4" />Eerste artikel toevoegen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100" data-testid={`blog-card-${p.slug}`}>
                <div className="aspect-video bg-gray-100 overflow-hidden">
                  {p.hero_image && <img src={p.hero_image} alt={p.title_nl} className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-gray-900 leading-tight">{p.title_nl}</h3>
                    {!p.published && <span className="text-[10px] uppercase bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full shrink-0">draft</span>}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">/inspiratie/{p.slug}</p>
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                    <a href={`/inspiratie/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs bg-gray-50 text-gray-700 px-2.5 py-1.5 rounded-full">
                      <ExternalLink className="w-3 h-3" />Preview
                    </a>
                    <button onClick={() => openEdit(p)} className="inline-flex items-center gap-1.5 text-xs bg-gray-900 text-white px-2.5 py-1.5 rounded-full">
                      <Edit3 className="w-3 h-3" />Bewerk
                    </button>
                    <button onClick={() => handleDelete(p)} className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-full">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 overflow-y-auto py-8 px-4" onClick={closeForm}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 p-5">
              <h2 className="text-xl font-medium">{editingId ? 'Artikel bewerken' : 'Nieuw artikel'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submitForm} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto" data-testid="blog-form">
              <div>
                <label className="block text-sm font-medium mb-1">Titel (NL) *</label>
                <input type="text" required value={form.title_nl} onChange={(e) => setForm({ ...form, title_nl: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" data-testid="blog-form-title-nl" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titel (EN) — optioneel</label>
                <input type="text" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">URL-slug — optioneel</label>
                <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="herfst-must-haves-2026" className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hero-afbeelding *</label>
                <div className="flex gap-2">
                  <input type="url" required value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} placeholder="URL of upload →" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="blog-form-hero" />
                  <button type="button" onClick={() => heroInputRef.current?.click()} disabled={uploadingHero} className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                    <Upload className="w-4 h-4" />{uploadingHero ? 'Uploaden…' : 'Upload'}
                  </button>
                  <input ref={heroInputRef} type="file" accept="image/*" onChange={handleHeroFile} className="hidden" />
                </div>
                {form.hero_image && <img src={form.hero_image} alt="preview" className="mt-2 w-32 h-32 object-cover rounded-lg border" />}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Samenvatting (NL) — 1-2 zinnen</label>
                <textarea rows={2} value={form.excerpt_nl} onChange={(e) => setForm({ ...form, excerpt_nl: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Samenvatting (EN) — optioneel</label>
                <textarea rows={2} value={form.excerpt_en} onChange={(e) => setForm({ ...form, excerpt_en: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tekst (NL) * — 800-1200 woorden aanbevolen</label>
                <textarea rows={12} required value={form.body_nl} onChange={(e) => setForm({ ...form, body_nl: e.target.value })} placeholder="Gebruik lege regels tussen alinea's." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" data-testid="blog-form-body-nl" />
                <p className="text-xs text-gray-400 mt-1">{form.body_nl.trim().split(/\s+/).filter(Boolean).length} woorden</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tekst (EN) — optioneel</label>
                <textarea rows={6} value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tags — komma-gescheiden</label>
                <input type="text" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="herfst, tijdloos, 40+" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                Publiceer meteen
              </label>
              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-medium disabled:opacity-50" data-testid="blog-form-submit">
                  <Save className="w-4 h-4" />{saving ? 'Opslaan…' : editingId ? 'Wijzigingen opslaan' : 'Artikel aanmaken'}
                </button>
                <button type="button" onClick={closeForm} className="text-sm text-gray-500 px-4">Annuleren</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogPage;
